import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useDispatch } from "react-redux";
import { setLoading, showToast } from "../../store/slices/common";
import PageLayout from "../../components/PageLayout";
import {
  getCuratedMatches,
  setCuratedMatches,
  removeCuratedMatch,
  resetCuratedMatchVotes,
} from "../../http/curatedMatches";
import ConfirmDialog from "./ConfirmDialog";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: "calc(100vh - 380px)",
    maxHeight: "calc(100vh - 380px)",
    boxShadow: "none !important",
  },
  table: {
    border: "1px solid rgba(224, 224, 224, 1)",
    minWidth: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  headerCell: {
    fontSize: "13px !important",
    color: "#000 !important",
    fontWeight: "600 !important",
    padding: "8px 6px !important",
    backgroundColor: "#e0e0e0",
  },
  bodyCell: {
    fontSize: "13px !important",
    color: "#333 !important",
    padding: "6px !important",
  },
  button: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  addSection: {
    padding: "20px",
    borderRadius: "6px",
    boxShadow:
      "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
    marginBottom: "24px",
  },
  listSection: {
    padding: "15px",
    borderRadius: "6px",
    boxShadow:
      "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  },
  playersColumn: {
    maxWidth: 140,
    "& span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "inline-block",
      maxWidth: "100%",
    },
  },
  playerCell: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  vsText: {
    margin: "0 4px",
    fontWeight: 600,
    color: "#999",
  },
  thumbnail: {
    width: 40,
    height: 40,
    borderRadius: 4,
    objectFit: "cover",
  },
  thumbnailWrapper: {
    position: "relative",
    cursor: "pointer",
    "&:hover $playOverlay": {
      opacity: 1,
    },
  },
  playOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.4)",
    borderRadius: 4,
    opacity: 0,
    transition: "opacity 0.2s",
  },
  idCell: {
    "& .id-line": {
      fontSize: "11px",
      fontFamily: "monospace",
      wordBreak: "break-all",
    },
  },
}));

const CuratedMatches = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [matchIdsInput, setMatchIdsInput] = useState("");
  const [curatedMatches, setCuratedMatchesList] = useState([]);

  const [videoDialog, setVideoDialog] = useState({ open: false, url: "" });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  useEffect(() => {
    fetchCuratedMatches();
  }, []);

  const fetchCuratedMatches = async () => {
    try {
      dispatch(setLoading(true));
      const res = await getCuratedMatches();
      const list = res?.data?.curatedMatches || [];
      setCuratedMatchesList(
        [...list].sort(
          (a, b) => (a.curatedOrder ?? 0) - (b.curatedOrder ?? 0)
        )
      );
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSyncMatches = async () => {
    const raw = matchIdsInput.trim();
    if (!raw) {
      dispatch(showToast({ color: "error", msg: "Please enter match IDs" }));
      return;
    }

    const matchIds = raw
      .split(/[\s,]+/)
      .map((id) => id.trim())
      .filter(Boolean);

    if (matchIds.length === 0) {
      dispatch(showToast({ color: "error", msg: "No valid match IDs found" }));
      return;
    }

    try {
      dispatch(setLoading(true));
      await setCuratedMatches(
        matchIds.map((originalMatchId) => ({ originalMatchId }))
      );
      dispatch(
        showToast({
          color: "success",
          msg: `Curated list synced with ${matchIds.length} match(es)`,
        })
      );
      setMatchIdsInput("");
      await fetchCuratedMatches();
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRemove = (match) => {
    setConfirmDialog({
      open: true,
      title: "Remove Curated Match",
      message:
        "Are you sure you want to remove this match from the curated list?",
      onConfirm: async () => {
        try {
          dispatch(setLoading(true));
          await removeCuratedMatch(match._id);
          dispatch(
            showToast({ color: "success", msg: "Match removed successfully" })
          );
          await fetchCuratedMatches();
        } catch (err) {
          console.log(err);
        } finally {
          dispatch(setLoading(false));
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
    });
  };

  const handleResetVotes = (match) => {
    setConfirmDialog({
      open: true,
      title: "Reset Votes",
      message:
        "Are you sure you want to reset all votes for this match? This cannot be undone.",
      onConfirm: async () => {
        try {
          dispatch(setLoading(true));
          await resetCuratedMatchVotes(match._id);
          dispatch(
            showToast({ color: "success", msg: "Votes reset successfully" })
          );
          await fetchCuratedMatches();
        } catch (err) {
          console.log(err);
        } finally {
          dispatch(setLoading(false));
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
    });
  };

  const handleReorder = async (fromIndex, direction) => {
    const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= curatedMatches.length) return;
    const next = [...curatedMatches];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    const curatedMatchesPayload = next.map((m) => ({
      originalMatchId: m.originalMatchId ?? m._id,
    }));
    try {
      dispatch(setLoading(true));
      await setCuratedMatches(curatedMatchesPayload);
      await fetchCuratedMatches();
      dispatch(
        showToast({ color: "success", msg: "Order updated" })
      );
    } catch (err) {
      console.log(err);
      dispatch(showToast({ color: "error", msg: "Failed to update order" }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <PageLayout title="Curated Matches">
      {/* Section 1: Add Matches */}
      <Box className={classes.addSection}>
        <Typography
          variant="h6"
          sx={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}
        >
          Add Matches
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#666", marginBottom: "12px" }}
        >
          Paste original match IDs below (comma-separated or one per line). This
          will sync the curated list to exactly what you submit.
        </Typography>
        <TextField
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          placeholder={"e.g. 6651a2b3c4d5e6f7a8b9c0d1, 6651a2b3c4d5e6f7a8b9c0d2"}
          value={matchIdsInput}
          onChange={(e) => setMatchIdsInput(e.target.value)}
          sx={{ marginBottom: "12px" }}
        />
        <Button
          variant="contained"
          className={classes.button}
          onClick={handleSyncMatches}
        >
          Sync Curated List
        </Button>
      </Box>

      {/* Section 2: Current Curated Matches */}
      <Box className={classes.listSection}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            marginBottom: "12px",
          }}
        >
          Current Curated Matches ({curatedMatches.length})
        </Typography>

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.stickyHeader}>
                <TableCell className={classes.headerCell}>#</TableCell>
                <TableCell className={classes.headerCell}>IDs</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.playersColumn}`}>Players</TableCell>
                <TableCell className={classes.headerCell}>Posts</TableCell>
                <TableCell className={classes.headerCell}>Category / Challenge</TableCell>
                <TableCell className={classes.headerCell}>Votes</TableCell>
                <TableCell className={classes.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {curatedMatches.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      textAlign: "center",
                      height: "200px",
                      fontSize: "14px",
                      color: "#999",
                    }}
                  >
                    No curated matches yet. Add match IDs above to get started.
                  </TableCell>
                </TableRow>
              ) : (
                curatedMatches.map((match, index) => (
                  <TableRow key={match._id}>
                    {/* Order */}
                    <TableCell className={classes.bodyCell}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600, minWidth: 20 }}
                        >
                          {match.curatedOrder ?? index + 1}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleReorder(index, "up")}
                                disabled={index === 0}
                                sx={{ padding: "2px" }}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleReorder(index, "down")}
                                disabled={index === curatedMatches.length - 1}
                                sx={{ padding: "2px" }}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </TableCell>
                    {/* IDs (original + match) */}
                    <TableCell
                      className={`${classes.bodyCell} ${classes.idCell}`}
                    >
                      <div className="id-line">
                        OrigID: {match.originalMatchId || "—"}
                      </div>
                      <div className="id-line">
                        MatchID: {match._id || "—"}
                      </div>
                    </TableCell>
                    {/* Players */}
                    <TableCell className={`${classes.bodyCell} ${classes.playersColumn}`}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flexWrap: "wrap",
                          minWidth: 0,
                        }}
                      >
                        <Box className={classes.playerCell}>
                          <span>{match.player1?.username || "—"}</span>
                        </Box>
                        <span className={classes.vsText}>vs</span>
                        <Box className={classes.playerCell}>
                          <span>{match.player2?.username || "—"}</span>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Posts */}
                    <TableCell className={classes.bodyCell}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {match.post1?.thumbnail ? (
                          <Box
                            className={classes.thumbnailWrapper}
                            onClick={() =>
                              match.post1?.media &&
                              setVideoDialog({ open: true, url: match.post1.media })
                            }
                          >
                            <img
                              src={match.post1.thumbnail}
                              alt="Post 1"
                              className={classes.thumbnail}
                            />
                            {match.post1?.media && (
                              <Box className={classes.playOverlay}>
                                <PlayCircleOutlineIcon
                                  sx={{ color: "#fff", fontSize: 24 }}
                                />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              backgroundColor: "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              color: "#999",
                            }}
                          >
                            N/A
                          </Box>
                        )}
                        <span className={classes.vsText}>vs</span>
                        {match.post2?.thumbnail ? (
                          <Box
                            className={classes.thumbnailWrapper}
                            onClick={() =>
                              match.post2?.media &&
                              setVideoDialog({ open: true, url: match.post2.media })
                            }
                          >
                            <img
                              src={match.post2.thumbnail}
                              alt="Post 2"
                              className={classes.thumbnail}
                            />
                            {match.post2?.media && (
                              <Box className={classes.playOverlay}>
                                <PlayCircleOutlineIcon
                                  sx={{ color: "#fff", fontSize: 24 }}
                                />
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              backgroundColor: "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              color: "#999",
                            }}
                          >
                            N/A
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    {/* Category / Challenge */}
                    <TableCell className={classes.bodyCell}>
                      <Box sx={{ fontSize: "12px" }}>
                        <div>{match.categoryName || "—"}</div>
                        <div style={{ color: "#666" }}>
                          {match.challengeTitle || "—"}
                        </div>
                      </Box>
                    </TableCell>

                    {/* Votes */}
                    <TableCell className={classes.bodyCell}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600, fontSize: "14px" }}
                        >
                          {match.post1Votes ?? 0}
                        </Typography>
                        <span className={classes.vsText}>:</span>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600, fontSize: "14px" }}
                        >
                          {match.post2Votes ?? 0}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className={classes.bodyCell}>
                      <Box sx={{ display: "flex", gap: "4px" }}>
                        <Tooltip title="Reset Votes">
                          <IconButton
                            size="small"
                            onClick={() => handleResetVotes(match)}
                            sx={{ color: "#f57c00" }}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => handleRemove(match)}
                            sx={{ color: "#e53935" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />

      {/* Video Player Dialog */}
      <Dialog
        open={videoDialog.open}
        onClose={() => setVideoDialog({ open: false, url: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 16px",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Post Video</Typography>
          <IconButton
            onClick={() => setVideoDialog({ open: false, url: "" })}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 16px 16px" }}>
          {videoDialog.url && (
            <video
              src={videoDialog.url}
              controls
              autoPlay
              style={{
                width: "100%",
                maxHeight: "70vh",
                borderRadius: 8,
                backgroundColor: "#000",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default CuratedMatches;
